import { Link } from 'react-router-dom';
import { CTA, PageHero, ProductShot } from '../../components/Common';
import { Seo } from '../../components/Seo';
import { track } from '../../components/analytics';
import { searchPages, site, type SearchPage } from '../../data/site';
import { Head } from './sections';

/**
 * One page per search query (`searchPages` in product metadata): the query's words in the URL,
 * title and h1, the matching capabilities, a short FAQ (also FAQPage JSON-LD), and links to the
 * sibling pages so each one passes weight to the others.
 */
export function SearchLanding({ page }: { page: SearchPage }) {
  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: site.name,
      applicationCategory: 'BusinessApplication',
      description: page.description,
      url: site.origin,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: page.faqs.map(({ question, answer }) => ({
        '@type': 'Question',
        name: question,
        acceptedAnswer: { '@type': 'Answer', text: answer },
      })),
    },
  ];
  const related = searchPages.filter(other => other.slug !== page.slug);
  return (
    <>
      <Seo
        title={page.title}
        breadcrumb={page.label}
        description={page.description}
        schema={schema}
      />
      <PageHero eyebrow={page.eyebrow} title={page.heading} description={page.description}>
        <div className="actions">
          <Link
            className="button primary"
            to="/start-trial"
            onClick={() => track('trial_cta_click', { placement: 'search_page' })}
          >
            Start free trial
          </Link>
          <Link className="button secondary" to="/request-demo">
            Request a demo
          </Link>
        </div>
      </PageHero>
      {page.shot && (
        <section className="section compact">
          <div className="container">
            <ProductShot slug={page.shot.slug} alt={page.shot.alt} className="motion-item" />
          </div>
        </section>
      )}
      <section className="section">
        <div className="container grid two">
          {page.sections.map(section => (
            <article className="card capability" key={section.heading}>
              <h2 className="card-title">{section.heading}</h2>
              <p>{section.body}</p>
              <ul className="tick-list">
                {section.bullets.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>
      <section className="section surface">
        <div className="container prose">
          <Head eyebrow="Questions" title={`${page.label}: common questions`} />
          <div className="faq-list">
            {page.faqs.map(({ question, answer }) => (
              <details key={question} className="faq-item">
                <summary>
                  {question}
                  <span aria-hidden="true">+</span>
                </summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <section className="section">
        <div className="container">
          <Head eyebrow="More from one workspace" title={`What else ${site.name} does`} />
          <ul className="tick-list">
            {related.map(other => (
              <li key={other.slug}>
                <Link to={`/${other.slug}`}>{other.label}</Link>
              </li>
            ))}
            <li>
              <Link to="/features">All features</Link>
            </li>
            <li>
              <Link to="/pricing">Pricing</Link>
            </li>
          </ul>
        </div>
      </section>
      <CTA />
    </>
  );
}
